// =============================================================================
// AuditBlockDialog — session open 409 (audit blocking violations) 共通ダイアログ
// (2026-07-15 blockfix)
//
//   背景: user の正常操作が一時的に blocking 監査 violation を生む構造があり
//   (例: 学習イベント書込の D1 一過性エラー欠落 / 新規 partner 承認登録の台帳未記録)、
//   /api/claude/session/open が 409 を返すと全 UI 操作が沈黙して止まっていた。
//
//   本コンポーネントは AppShell に常駐し、api/client.ts の notifyAuditBlocked が
//   dispatch する CustomEvent (AUDIT_BLOCKED_EVENT) を購読して:
//     1. violations を日本語で列挙 (rule 名 / 件数 / fix)
//     2. [自動修復を試す] → POST /api/admin/auto-fix-audit-violations
//        (confirm_token 固定・worker 側は決定論 repairer のみ = 推測ゼロ)
//     3. 修復結果を表示 → 成功時 session open を自動再試行 → 解消なら成功表示
//
//   防御実装: 409 body が無い / violations が無い / auto-fix 応答が空でも壊れない。
// =============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUDIT_BLOCKED_EVENT,
  AuditBlockViolation,
  runAutoFixAuditViolations,
  retryClaudeSessionOpen,
} from '../api/client';

// rule 名 → 日本語ラベル (未知 rule は raw 名を表示)
const RULE_LABELS_JA: Record<string, string> = {
  pattern_learning: '取引先パターン学習が未記録',
  pattern_accumulation_delay: '登録後のパターン記録遅延',
  bank_learning_event_skip: '銀行学習イベントの欠落',
  receipts_attached: '登録済み取引に証憑未添付',
  freee_auto_register_failures: '自動登録の失敗残留',
  approved_proposal_stale: '承認済みのまま未登録 (24h+)',
  unhandled_instructions: '未処理の指示 (instructions)',
  registration_without_approval: '承認なし登録 (順序違反)',
  invoice_date_not_month_end: '請求書の発生日が月末でない',
  due_date_not_business_day: '支払期日が営業日でない',
  proposal_missing_name_fields: '提案の名称フィールド欠落',
  proposal_amount_mismatch: '提案金額の不整合',
  gates_not_passed: '必須ゲート未通過',
  ref_number_duplicates: 'ref_number 重複',
  partner_id_mismatch: 'partner_id 不整合',
  proposal_missing_audit_summary: '提案の監査サマリ欠落',
  department_estimation_marker: '部門の推測マーカー残留',
  new_partner_without_investigation: '新規取引先の調査未実施',
  multi_stage_reading_missing: '多段読取の未実施',
  duplicate_composite_key: '複合キー重複',
  receipt_serial_pdf_only: 'PDF 証憑参照の欠落',
  freee_deal_id_shared_across_refs: 'freee_deal_id の複数 ref 共有',
  approve_register_ordering: '承認→登録の順序違反',
  receipts_fetched_without_filter: 'レシート全件取得 (filter なし)',
  api_pagination_incomplete: 'API ページネーション不完全',
  background_freee_forbidden: 'background での freee 操作',
  aggregate_invoice_double_count: '合算請求書の二重計上疑い',
  section_name_mismatch: '部門名の canonical 不一致',
  detail_pattern_unverified: '過去明細パターン未確認',
  item_name_mismatch: '品目名の canonical 不一致',
  account_name_mismatch: '勘定科目名の canonical 不一致',
  fabricated_past_details: '過去明細の捏造疑い',
  build_trigger_failure: 'proposal 自動生成チェーン失敗',
  item_name_free_text: '品目名の自由記述 (想像補填)',
  proposal_without_instruction_id: 'instruction 紐付けなしの提案',
  bank_flow_dispatch_integrity: '銀行フロー dispatch 整合性違反',
  bank_entry_without_session_lease: 'session lease なしの銀行処理',
  bank_deal_with_payments: '銀行 deal の payments 混入',
  auto_register_rule_fingerprint_collision: '自動登録ルールの重複',
  arica_total_amount_adopted: 'ARICA 合計金額の誤採用',
  stage1_non_promoted_template_adopted: '未昇格テンプレートの採用',
  bank_stage1_strict_flow_violation: '銀行 Stage1 厳格フロー違反',
  bank_deal_due_residual_drift: '銀行入金と deal 金額の不整合',
  claude_cron_autoclaim: 'cron 指示の無承認 claim',
  processing_flags_missing_for_active_proposal: 'processing_flags 欠落',
  past_details_consultation_required_but_undone: '過去明細参照の未実施',
  canonical_provenance_undone: 'canonical 解決の未実施',
  amount_3way_validation_undone: '金額 3-way 検証の未実施',
  account_name_provenance_canonical_fallback: '勘定科目の出所が fallback',
  arica_vision_partner_mismatch: 'vision 抽出の取引先不一致',
  claude_cli_freee_deals_direct_post: 'CLI からの freee 直接 POST',
  manual_oauth_url_bypass: '手動 OAuth URL バイパス',
  worker_register_via_worker_without_approval: '承認なし register-via-worker',
  nippou_learning_pending: '日報の未知分類が学習待ち',
};

type Phase = 'idle' | 'fixing' | 'retrying' | 'resolved' | 'still_blocked' | 'fix_failed';

interface BlockState {
  violations: AuditBlockViolation[];
  blockingCount: number | null;
  message: string | null;
  receivedAt: number;
}

function toBlockState(detail: any): BlockState {
  // 防御: detail / violations が無い・配列でない場合も壊れない
  const violations: AuditBlockViolation[] = Array.isArray(detail?.violations)
    ? detail.violations.filter((v: any) => v && typeof v === 'object')
    : [];
  const blockingCount = Number.isFinite(Number(detail?.blocking_count))
    ? Number(detail.blocking_count)
    : null;
  return {
    violations,
    blockingCount,
    message: typeof detail?.message === 'string' ? detail.message : null,
    receivedAt: Date.now(),
  };
}

function isBlockingSeverity(v: AuditBlockViolation): boolean {
  return v.severity === 'block' || v.severity === 'critical';
}

function summarizeFixResult(result: any): string[] {
  // worker 応答: { summary: { rule: {scanned, fixed, rejected, untouched} }, actions: [...] }
  const lines: string[] = [];
  const summary = result && typeof result === 'object' ? result.summary : null;
  if (summary && typeof summary === 'object') {
    for (const [rule, s] of Object.entries<any>(summary)) {
      if (!s || typeof s !== 'object') continue;
      const fixed = Number(s.fixed || 0);
      const rejected = Number(s.rejected || 0);
      const untouched = Number(s.untouched || 0);
      const scanned = Number(s.scanned || 0);
      if (fixed === 0 && rejected === 0 && untouched === 0 && scanned === 0) continue;
      const label = RULE_LABELS_JA[rule] || rule;
      const parts: string[] = [];
      if (fixed > 0) parts.push(`修復 ${fixed}件`);
      if (rejected > 0) parts.push(`機械 reject ${rejected}件`);
      if (untouched > 0) parts.push(`対象外 ${untouched}件`);
      if (parts.length === 0) parts.push(`対象 ${scanned}件 (変更なし)`);
      lines.push(`${label}: ${parts.join(' / ')}`);
    }
  }
  if (lines.length === 0) lines.push('自動修復対象はありませんでした (repairer 対象外の違反)');
  return lines;
}

const AuditBlockDialog: React.FC = () => {
  const [block, setBlock] = useState<BlockState | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [fixLines, setFixLines] = useState<string[]>([]);
  const [statusText, setStatusText] = useState<string | null>(null);
  const phaseRef = useRef<Phase>('idle');
  phaseRef.current = phase;

  useEffect(() => {
    const onBlocked = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      // 修復実行中 / 再試行中に追加の 409 が来ても表示を巻き戻さない
      if (phaseRef.current === 'fixing' || phaseRef.current === 'retrying') return;
      setBlock(toBlockState(detail));
      setPhase('idle');
      setFixLines([]);
      setStatusText(null);
    };
    window.addEventListener(AUDIT_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(AUDIT_BLOCKED_EVENT, onBlocked);
  }, []);

  const close = useCallback(() => {
    setBlock(null);
    setPhase('idle');
    setFixLines([]);
    setStatusText(null);
  }, []);

  const runAutoFix = useCallback(async () => {
    setPhase('fixing');
    setStatusText('自動修復を実行中… (決定論 repairer のみ・freee 書込なし)');
    setFixLines([]);
    let fixResult: any = null;
    try {
      fixResult = await runAutoFixAuditViolations();
    } catch (e: unknown) {
      setPhase('fix_failed');
      setStatusText(`自動修復に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    setFixLines(summarizeFixResult(fixResult));
    // 修復後に session open を再試行
    setPhase('retrying');
    setStatusText('session open を再試行中…');
    const retry = await retryClaudeSessionOpen();
    if (retry.ok) {
      setPhase('resolved');
      setStatusText('解消しました。セッションを再取得済みです — 直前の操作をもう一度実行してください。');
      // 成功は 8 秒後に自動で片付け
      window.setTimeout(() => {
        setBlock((b) => (b && phaseRef.current === 'resolved' ? null : b));
      }, 8000);
    } else if (retry.status === 409) {
      // まだ blocking が残っている → 最新 violations に更新して表示継続
      setBlock(toBlockState(retry.body));
      setPhase('still_blocked');
      setStatusText('自動修復では解消しきれない違反が残っています (Claude / 手動対応が必要)。');
    } else {
      setPhase('fix_failed');
      setStatusText(`session open 再試行が失敗しました (status=${retry.status || 'network'})。時間をおいて再試行してください。`);
    }
  }, []);

  if (!block) return null;

  const blocking = block.violations.filter(isBlockingSeverity);
  const warns = block.violations.filter((v) => !isBlockingSeverity(v));
  const busy = phase === 'fixing' || phase === 'retrying';
  const resolved = phase === 'resolved';

  return (
    <div
      role="alertdialog"
      aria-label="監査ブロックにより操作が停止しています"
      style={{
        position: 'fixed', right: 16, bottom: 16, zIndex: 9999,
        width: 'min(480px, calc(100vw - 32px))',
        background: '#fff',
        border: `2px solid ${resolved ? '#2e7d52' : '#b3564d'}`,
        borderRadius: 10,
        boxShadow: '0 10px 32px rgba(32,58,87,.28)',
        fontSize: 13,
        color: '#26313d',
        overflow: 'hidden',
      }}
    >
      {/* header */}
      <div style={{
        padding: '10px 14px',
        background: resolved ? '#2e7d52' : '#b3564d',
        color: '#fff', fontWeight: 800, letterSpacing: '.03em',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ flex: 1 }}>
          {resolved ? '監査ブロック解消' : '監査ブロック — 操作が一時停止中'}
        </span>
        <button
          type="button"
          onClick={close}
          disabled={busy}
          style={{
            background: 'transparent', border: 'none', color: '#fff',
            fontSize: 16, fontWeight: 800, cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.5 : 1, lineHeight: 1, padding: '2px 4px',
          }}
          aria-label="閉じる"
        >
          ×
        </button>
      </div>

      <div style={{ padding: '12px 14px', maxHeight: '55vh', overflowY: 'auto' }}>
        {!resolved && (
          <p style={{ margin: '0 0 8px', lineHeight: 1.55 }}>
            {block.message || 'blocking 違反があり session open が 409 で拒否されました。'}
            {block.blockingCount != null && (
              <span style={{ fontWeight: 700 }}> (blocking {block.blockingCount}件)</span>
            )}
          </p>
        )}

        {/* violations 列挙 (防御: 0 件でも表示が壊れない) */}
        {!resolved && blocking.length > 0 && (
          <ul style={{ margin: '0 0 10px', padding: '0 0 0 18px', display: 'grid', gap: 8 }}>
            {blocking.map((v, i) => (
              <li key={`${v.rule || 'unknown'}-${i}`} style={{ lineHeight: 1.5 }}>
                <span style={{ fontWeight: 800 }}>
                  {RULE_LABELS_JA[String(v.rule || '')] || v.rule || '不明な違反'}
                </span>
                {typeof v.count === 'number' && (
                  <span style={{ fontWeight: 700 }}> — {v.count}件</span>
                )}
                {v.severity === 'critical' && (
                  <span style={{
                    marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10.5,
                    fontWeight: 800, background: '#7a2e28', color: '#fff',
                  }}>critical</span>
                )}
                {v.fix && (
                  <div style={{ fontSize: 12, color: '#5a636e', marginTop: 2 }}>
                    対処: {v.fix}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {!resolved && blocking.length === 0 && (
          <p style={{ margin: '0 0 10px', color: '#5a636e' }}>
            違反の詳細を取得できませんでした (応答 body なし)。自動修復を試すか、時間をおいて再度操作してください。
          </p>
        )}
        {!resolved && warns.length > 0 && (
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#7b6a37' }}>
            ほかに warn 級 {warns.length}件 (session open は妨げません — 自己回復猶予中の項目を含む)
          </p>
        )}

        {/* 自動修復の結果表示 */}
        {fixLines.length > 0 && (
          <div style={{
            margin: '0 0 10px', padding: '8px 10px', borderRadius: 8,
            background: '#f4f6f2', border: '1px solid #d8ded2', fontSize: 12.5,
          }}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>自動修復の結果</div>
            {fixLines.map((l, i) => <div key={i} style={{ lineHeight: 1.5 }}>{l}</div>)}
          </div>
        )}

        {statusText && (
          <p style={{
            margin: '0 0 4px', fontWeight: 700, lineHeight: 1.5,
            color: resolved ? '#2e7d52' : phase === 'fix_failed' || phase === 'still_blocked' ? '#b3564d' : '#26313d',
          }}>
            {statusText}
          </p>
        )}
      </div>

      {/* footer */}
      <div style={{
        padding: '10px 14px', borderTop: '1px solid #e3e7ec',
        display: 'flex', gap: 8, justifyContent: 'flex-end', background: '#fafbfc',
      }}>
        {!resolved && (
          <button
            type="button"
            onClick={runAutoFix}
            disabled={busy}
            style={{
              padding: '7px 14px', borderRadius: 8, border: 'none',
              background: busy ? '#8fa6bf' : '#203a57', color: '#fff',
              fontWeight: 800, fontSize: 13, cursor: busy ? 'default' : 'pointer',
            }}
          >
            {phase === 'fixing' ? '修復中…' : phase === 'retrying' ? '再試行中…' : '自動修復を試す'}
          </button>
        )}
        <button
          type="button"
          onClick={close}
          disabled={busy}
          style={{
            padding: '7px 14px', borderRadius: 8, border: '1px solid #c6cdd6',
            background: '#fff', color: '#26313d', fontWeight: 700, fontSize: 13,
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          }}
        >
          閉じる
        </button>
      </div>
    </div>
  );
};

export default AuditBlockDialog;
